import * as React from 'react'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { NewCardPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import CardTypeSelector from 'modules/expenses/components/cards/CardTypeSelector'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import SingleUseCardNotification from '../components/_Notification/SingleUseCardNotification'
import { CardCreationConfirmationModal } from '../components/cards/CardCreationConfirmationModal'
import { CardForm } from '../components/cards/PageContents/NewCard/CardForm'
import SubmitCancelButtons from '../components/cards/PageContents/NewCard/SubmitCancelButtons'
import { useCardForm } from '../components/cards/PageContents/NewCard/useCardForm'
import { usePhysicalCardsCreationLimits } from '../hooks/useCardsCreationLimits'

const NewCardPage = () => {
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState<boolean>(false)
  const { company } = useCurrentSession()
  const { data, loading, error } = useQuery(NewCardPageDocument, {
    variables: { companyId: company.id, featureId: Features.expenses_physical_cards },
  })

  const featureAvailability: FeatureAvailabilityType = data?.companies
    .featureAvailabilitiesConnection.nodes[0] ?? {
    available: false,
    id: Features.expenses_physical_cards,
    _internalType: 'feature',
  }

  const { physicalCardsAvailability } = usePhysicalCardsCreationLimits(featureAvailability)
  const canManageCompanyPermissions = !!data?.apiCore?._policies?.companyManagePermissions?.can
  const currentCompany = data?.apiCore?.currentsConnection?.nodes[0]?.company
  const mainLegalEntity = (currentCompany?.legalEntities || []).find(
    (legalEntity) => legalEntity.main
  )

  const {
    form,
    loadingCardholder,
    selectedEmployeeInformation,
    hasAccount,
    selectedLegalEntity,
    cardsAvailableInCountry,
    submitDisabled,
  } = useCardForm(mainLegalEntity)

  const submitForm = async () => {
    const errors = await form.validateForm()
    if (!errors || Object.keys(errors).length === 0) {
      form.values.cardType == 'physical' && hasFeature('dev_packaging_paywalls')
        ? setConfirmationModalOpen(true)
        : form.handleSubmit()
    }
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      authorized={!!data?.expenses._policies.cardManageCards.can}
    >
      <Box width="full">
        <SingleUseCardNotification canManageCompanyPermissions={canManageCompanyPermissions} />
        <Box flexDirection="row" paddingX="s120" justifyContent="center" alignItems="center">
          <Form>
            <Box alignItems="center">
              <CardTypeSelector form={form} />
              {form.values.cardType && (
                <CardForm
                  loadingCardholder={loadingCardholder}
                  form={form}
                  selectedEmployeeInformation={selectedEmployeeInformation}
                  mainLegalEntity={mainLegalEntity}
                  hasAccount={hasAccount}
                  cardsAvailableInCountry={cardsAvailableInCountry}
                  selectedLegalEntity={selectedLegalEntity}
                />
              )}
            </Box>
          </Form>
        </Box>
      </Box>
      {confirmationModalOpen && (
        <CardCreationConfirmationModal
          icon="alert"
          title={i18n.t('expenses_v2.list.actions.modal.title_physical_card_creation')}
          description={i18n.t('expenses_v2.list.actions.modal.description_physical_card_creation')}
          warningMessage={physicalCardsAvailability.warningMessage}
          onSubmit={form.handleSubmit}
          onClose={() => {
            setConfirmationModalOpen(false)
          }}
          submitDisabled={submitDisabled}
        />
      )}
      <SubmitCancelButtons submitDisabled={submitDisabled} submitForm={submitForm} />
    </ResponsivePage.Body>
  )
}

export default NewCardPage
